import { useEffect, useMemo, useRef, useState } from 'react';

import { connectorsConfig } from '@atlassian/rovo-connectors-config/facade';
import { isProductResourceTypes3P } from '@atlassian/rovo-connectors-config/isProductResourceTypes3P';
import type { RovoLogo } from '@atlassian/rovo-connectors-config/icons';
import type { ProductKeys3P } from '@atlassian/rovo-connectors-config/types';
import { get3pSearchConfig } from '@atlassian/search-client/fetch-search-config';
import {
	detectEnvFromPayload,
	fetchOutboundAuthAccounts,
	isAuthenticatedForProduct,
} from '@atlassian/confluence-account-setup/entry-points/outbound-auth';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

/** Matches the CWR ghost icons, which show the top two connectors. */
export const MAX_CONNECTABLE_APPS = 2;

export type ConnectableApp = {
	productKey: ProductKeys3P;
	/** Connector config id (e.g. `notion`); used for test ids, not shown to users. */
	id: string;
	Icon: RovoLogo;
};

export type UseConnectableAppsResult = {
	/** Highest org adoption first. */
	apps: ConnectableApp[];
};

const EMPTY_APPS: ConnectableApp[] = [];

type UseConnectableAppsInput = {
	/**
	 * Always shown first, even when the 3P config never listed it as connectable — the
	 * variant that pins an app has already established the user can connect it, and its
	 * copy names it, so the icon has to be there.
	 */
	pinnedProductKey?: ProductKeys3P;
	/**
	 * Adoption percentages keyed by `ProductKeys3P`, highest shown first. The caller picks
	 * the trait so a banner can rank its icons by the same numbers its copy quotes.
	 */
	rates?: Record<string, number>;
};

/**
 * Admin-enabled 3P connectors this user has not authenticated with, ranked by org
 * adoption.
 *
 * Two sources, each for what it is reliable at. The 3P search config enumerates what the
 * site offers, but its `isUserOAuthed` is not trustworthy — `@atlassian/confluence-account-setup`
 * documents that it misses connections made through fallback paths, which is how a surface
 * ends up re-prompting someone already connected. So connectedness comes from the
 * outbound-auth gateway instead, the same source the Drive variant's own gate reads.
 *
 * Fails open at every step — these icons decorate a banner that must render regardless.
 */
export const useConnectableApps = ({
	pinnedProductKey,
	rates,
}: UseConnectableAppsInput = {}): UseConnectableAppsResult => {
	const { cloudId, userId } = useSessionData();

	// `undefined` until the fetch settles, distinguishing "loading" from "none qualified".
	const [unconnectedProductKeys, setUnconnectedProductKeys] = useState<ProductKeys3P[]>();
	const [apps, setApps] = useState<ConnectableApp[]>(EMPTY_APPS);

	useEffect(() => {
		if (!cloudId || !userId) {
			setUnconnectedProductKeys([]);
			return;
		}

		let isCurrent = true;

		Promise.all([get3pSearchConfig({ cloudId, userId }), fetchOutboundAuthAccounts(userId)])
			.then(([resources, grants]) => {
				if (!isCurrent) {
					return;
				}
				const env = detectEnvFromPayload(grants) ?? undefined;
				setUnconnectedProductKeys(
					resources.reduce<ProductKeys3P[]>((keys, resource) => {
						if (!isProductResourceTypes3P(resource.type)) {
							return keys;
						}
						const productKey = connectorsConfig.getProductKey(resource.type);
						if (
							!productKey ||
							keys.includes(productKey) ||
							isAuthenticatedForProduct(grants, productKey, env)
						) {
							return keys;
						}
						keys.push(productKey);
						return keys;
					}, []),
				);
			})
			.catch(() => {
				if (isCurrent) {
					setUnconnectedProductKeys([]);
				}
			});

		return () => {
			isCurrent = false;
		};
	}, [cloudId, userId]);

	// Ranked before loading configs so only the winning two are imported.
	const topProductKeys = useMemo(() => {
		if (unconnectedProductKeys === undefined) {
			return undefined;
		}
		const ranked = [...unconnectedProductKeys]
			.filter((key) => key !== pinnedProductKey)
			.sort((a, b) => (rates?.[b] ?? 0) - (rates?.[a] ?? 0));
		return (pinnedProductKey ? [pinnedProductKey, ...ranked] : ranked).slice(
			0,
			MAX_CONNECTABLE_APPS,
		);
	}, [unconnectedProductKeys, rates, pinnedProductKey]);

	// The effect below keys off the contents rather than the array, so a caller passing a
	// fresh `rates` object each render cannot spin it: new identity, same keys, no refetch.
	const topProductKeysId = topProductKeys?.join(',');
	const topProductKeysRef = useRef(topProductKeys);
	topProductKeysRef.current = topProductKeys;

	// Each config is a separate lazy chunk, hence a second async step rather than a
	// synchronous registry of every connector.
	useEffect(() => {
		const keys = topProductKeysRef.current;
		if (keys === undefined || keys.length === 0) {
			setApps(EMPTY_APPS);
			return;
		}

		let isCurrent = true;

		Promise.all(keys.map((productKey) => connectorsConfig.getConfig(productKey)))
			.then((configs) => {
				if (!isCurrent) {
					return;
				}
				setApps(
					configs.flatMap((config, index) =>
						config
							? [
									{
										productKey: keys[index],
										id: config.id,
										// Not `iconBorderless`: the pile draws no container, so each logo
										// supplies its own shape.
										Icon: config.icon,
									},
								]
							: [],
					),
				);
			})
			.catch(() => {
				if (isCurrent) {
					setApps(EMPTY_APPS);
				}
			});

		return () => {
			isCurrent = false;
		};
	}, [topProductKeysId]);

	return {
		apps,
	};
};
